import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Ruler, Plus, Search, Pen, Trash2, X as IconX, Type, CheckCircle2, XCircle, UploadCloud, Link as IconLink, Power } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchWithAuth } from '../../utils/api';
import { CustomSelect } from '../../components/CustomSelect';
import { BulkUploadModal } from '../../components/BulkUploadModal';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';

let nextTempId = 1;

const blank = () => ({ 
  id: null, 
  name: '', 
  status: 'Active',
  autoName: true,
  levels: [
    { id: Date.now().toString(), name: '', multiplier: null }
  ]
});

const HierarchyPreview = ({ levels }) => {
  return (
    <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-1">
      {levels.map((lvl, idx) => (
        <React.Fragment key={lvl.id || idx}>
          <div className="px-2 py-1 rounded-md border border-[var(--theme-primary)] text-[var(--theme-primary)] font-bold text-[10px] bg-white whitespace-nowrap">
            {lvl.name || '?'}
          </div>
          {idx < levels.length - 1 && (
            <div className="flex flex-col items-center shrink-0">
              <span className="text-[10px] font-bold text-slate-400 mb-[1px]">{lvl.multiplier || '1'}</span>
              <div className="w-4 h-px bg-slate-300 relative">
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-px w-1 h-1 border-t border-r border-slate-300 rotate-45"></div>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

HierarchyPreview.propTypes = {
  levels: PropTypes.array.isRequired
};

const Modal = ({ open, draft, setDraft, onClose, onSubmit, mode, accent, showNotification }) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  if (!open) return null;

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Please fill the Unit Hierarchy Name.';
    
    let invalidLevels = false;
    draft.levels.forEach((lvl, idx) => {
      if (!lvl.name || !lvl.name.trim()) invalidLevels = true;
      if (idx < draft.levels.length - 1) {
        if (!lvl.multiplier || isNaN(parseFloat(lvl.multiplier))) invalidLevels = true;
      }
    });

    if (invalidLevels) {
       if (showNotification) showNotification('Please fill all level names and conversion rates.', 'error');
       return;
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };

  const addLevel = () => {
    const newLevels = [
      ...draft.levels.slice(0, -1),
      { ...draft.levels[draft.levels.length - 1], multiplier: 1 },
      { id: Date.now().toString(), name: '', multiplier: null }
    ];
    let newName = draft.name;
    if (draft.autoName !== false) {
      newName = newLevels.map(l => l.name).filter(Boolean).join(' - ');
    }
    setDraft({ ...draft, levels: newLevels, name: newName });
  };

  const removeLevel = (index) => {
    let newLevels = [...draft.levels];
    newLevels.splice(index, 1);
    if (newLevels.length > 0) {
      newLevels[newLevels.length - 1].multiplier = null;
    }
    let newName = draft.name;
    if (draft.autoName !== false) {
      newName = newLevels.map(l => l.name).filter(Boolean).join(' - ');
    }
    setDraft({ ...draft, levels: newLevels, name: newName });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col max-h-[90dvh]"
        >
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}><Ruler size={18} /></div>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit Unit' : 'New Unit'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Unit of Measurement</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
          </header>

          <div className="px-6 py-5 flex flex-col gap-6 overflow-y-auto custom-scrollbar">
            
            <label className="block space-y-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                Unit Hierarchy Name
              </span>
              <span className="relative block">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Type size={14} /></span>
                <input
                  type="text"
                  value={draft.name}
                  readOnly
                  placeholder="Auto-generated from levels"
                  className={`w-full rounded-lg bg-slate-50 border ${errors.name ? 'border-rose-400 focus:border-rose-500' : 'border-[var(--theme-tertiary)] focus:border-[var(--theme-tertiary)]'} outline-none pl-9 pr-3 py-2.5 text-xs font-semibold text-slate-500 cursor-not-allowed`}
                />
              </span>
              {errors.name && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.name}</p>}
            </label>

            <div className="block space-y-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                Unit Hierarchy Preview
              </span>
              <div className="pt-1">
                <HierarchyPreview levels={draft.levels} />
              </div>
            </div>

            <div className="block space-y-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                Hierarchy Levels
              </span>
              <div className="flex flex-col gap-4">
                {draft.levels.map((lvl, idx) => (
                  <div key={lvl.id || idx} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm relative">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded bg-slate-600 text-white flex items-center justify-center font-bold text-[11px]">
                          {idx + 1}
                        </div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
                          Level {idx + 1} ({idx === 0 ? 'PARENT UNIT' : 'CHILD UNIT'})
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Conversion Text */}
                        {idx < draft.levels.length - 1 && lvl.name && draft.levels[idx + 1]?.name && lvl.multiplier && (
                          <div className="text-[11px] font-medium text-emerald-600">
                            1 {lvl.name} contains {lvl.multiplier} {draft.levels[idx + 1].name}
                          </div>
                        )}
                        {/* Delete button */}
                        {idx > 0 && idx === draft.levels.length - 1 && (
                          <button
                            type="button"
                            onClick={() => removeLevel(idx)}
                            className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      {/* UNIT NAME */}
                      <div className="flex-1 w-full">
                        <label className="block mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                          Unit Name
                        </label>
                        <span className="relative block">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Ruler size={14} /></span>
                          <input
                            type="text"
                            value={lvl.name}
                            onChange={(e) => {
                              const newLevels = [...draft.levels];
                              newLevels[idx].name = e.target.value;
                              let newName = draft.name;
                              if (draft.autoName !== false) {
                                newName = newLevels.map(l => l.name).filter(Boolean).join(' - ');
                              }
                              setDraft({ ...draft, levels: newLevels, name: newName });
                            }}
                            placeholder={idx === 0 ? "e.g. Box" : "e.g. Packet"}
                            className="w-full rounded-lg bg-white border border-gray-200 focus:border-[var(--theme-primary)] outline-none pl-9 pr-3 py-2 text-xs font-medium text-[#0d0d0d]"
                          />
                        </span>
                      </div>

                      {/* CONTAINS & NEXT UNIT (if not last level) */}
                      {idx < draft.levels.length - 1 && (
                        <>
                          <div className="w-full sm:w-28 shrink-0">
                            <label className="block mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                              Contains
                            </label>
                            <input
                              type="number"
                              min="0.01"
                              step="any"
                              value={lvl.multiplier || ''}
                              onChange={(e) => {
                                const newLevels = [...draft.levels];
                                newLevels[idx].multiplier = e.target.value;
                                setDraft({ ...draft, levels: newLevels });
                              }}
                              className="w-full rounded-lg bg-white border border-gray-200 focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] text-center"
                            />
                          </div>

                          <div className="w-full sm:w-32 shrink-0">
                            <label className="block mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                              Next Unit
                            </label>
                            <div className="w-full rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 flex justify-between items-center cursor-not-allowed">
                              <span>{draft.levels[idx + 1]?.name || 'Level ' + (idx + 2)}</span>
                              <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                  </div>
                ))}

                <button
                  type="button"
                  onClick={addLevel}
                  className="mt-2 w-full py-2.5 border border-dashed border-slate-300 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--theme-primary)] hover:bg-[var(--theme-primary-tint)] hover:border-[var(--theme-primary)] transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={14} /> Add Level
                </button>
              </div>
            </div>

            <label className="block space-y-1 mt-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Status <span className="text-rose-500 ml-1">*</span>
              </span>
              <CustomSelect
                value={draft.status}
                onChange={(val) => setDraft({ ...draft, status: val })}
                options={[
                  { value: 'Active', label: 'Active', icon: <Power size={14} className="text-emerald-500" /> },
                  { value: 'Inactive', label: 'Inactive', icon: <Power size={14} className="text-slate-400" /> }
                ]}
                className="w-full bg-gray-50 border-gray-100"
                menuPlacement="bottom"
              />
            </label>
          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
              style={{ background: accent }}
            >
              {mode === 'edit' ? 'Save' : 'Create Unit'}
            </button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
};

export const UnitsTab = ({ org, showNotification }) => {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [draft, setDraft] = useState(blank());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUnits = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await fetchWithAuth('/org/units');
      if (res.ok) {
        const data = await res.json();
        setUnits(Array.isArray(data) ? data : []);
      } else {
        throw new Error('Network error');
      }
    } catch (err) {
      if (showNotification) showNotification('Failed to fetch units: ' + err.message, 'error');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [org.id]);

  const list = useMemo(() => {
    if (!search.trim()) return units;
    const q = search.toLowerCase();
    return units.filter((u) => (u.name || '').toLowerCase().includes(q));
  }, [units, search]);

  const accent = org.theme.primary;

  const openCreate = () => { setDraft(blank()); setMode('create'); setModalOpen(true); };
  
  const openEdit = (rec) => { 
    let hierarchy = {};
    if (rec.hierarchy) {
      try {
        hierarchy = typeof rec.hierarchy === 'string' ? JSON.parse(rec.hierarchy) : rec.hierarchy;
      } catch {
        hierarchy = {};
      }
    }
    
    let initialLevels = [
      { id: `temp-${nextTempId++}`, name: rec.name, multiplier: 1 }
    ];
    
    if (hierarchy.levels && Array.isArray(hierarchy.levels)) {
      initialLevels = hierarchy.levels.map((l, i) => ({
        id: l.id || `temp-${nextTempId++}-${i}`,
        name: l.name || '',
        multiplier: l.multiplier || null
      }));
    } else if (hierarchy.baseUnit) {
      // Legacy data mapping
      initialLevels = [
        { id: `temp-${nextTempId++}`, name: rec.name, multiplier: hierarchy.conversionRate || 1 },
        { id: `temp-${nextTempId++}`, name: hierarchy.baseUnit, multiplier: null }
      ];
    }
    
    setDraft({ 
      id: rec.id,
      name: hierarchy.unitHierarchyName || rec.name, // prefer hierarchy name if available
      status: rec.status,
      autoName: false,
      levels: initialLevels
    }); 
    setMode('edit'); 
    setModalOpen(true); 
  };

  const submit = async (record) => {
    try {
      const payload = {
        id: record.id,
        name: record.name,
        status: record.status,
        hierarchy: { 
          unitHierarchyName: record.name,
          levels: record.levels.map(l => ({ name: l.name, multiplier: l.multiplier ? Number(l.multiplier) : null }))
        }
      };

      if (mode === 'create') {
        await fetchWithAuth('/org/units', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        await fetchWithAuth(`/org/units/${record.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      if (showNotification) showNotification(`${mode === 'create' ? 'Created' : 'Updated'} unit ${record.name}`, 'success');
      setModalOpen(false);
      fetchUnits(false); // fetch seamlessly
    } catch (err) {
      if (showNotification) showNotification(`Failed to ${mode} unit: ` + err.message, 'error');
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await fetchWithAuth(`/org/units/${confirmDelete.id}`, {
        method: 'DELETE'
      });
      if (showNotification) showNotification(`Removed unit ${confirmDelete.name}`, 'success');
      setConfirmDelete(null);
      fetchUnits(false); // fetch seamlessly
    } catch (err) {
      if (showNotification) showNotification('Failed to delete unit: ' + err.message, 'error');
    }
  };

  const toggleStatus = async (u) => {
    const currentStatus = u.status || 'Active';
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    try {
      let hierarchy = {};
      if (u.hierarchy) {
        try {
          hierarchy = typeof u.hierarchy === 'string' ? JSON.parse(u.hierarchy) : u.hierarchy;
        } catch { /* ignore invalid json */ }
      }
      const payload = {
        id: u.id,
        name: u.name,
        status: newStatus,
        hierarchy: hierarchy
      };
      const res = await fetchWithAuth(`/org/units/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Network response was not ok');
      if (showNotification) showNotification(`Unit marked as ${newStatus}`, 'success');
      fetchUnits(false);
    } catch (err) {
      if (showNotification) showNotification('Failed to update status: ' + err.message, 'error');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search units…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>
          </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setBulkModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 text-xs font-semibold uppercase tracking-[0.14em] bg-white transition-colors"
          >
            <UploadCloud size={16} />
            Bulk Upload
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg"
            style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
          >
            <Plus size={14} aria-hidden="true" /> New Unit
          </button>
        </div>
      </section>

      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">Loading units...</div>
      ) : (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg h-fit overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead>
              <tr className="border-b border-[var(--theme-tertiary)] bg-gray-50/50">
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[30%]">Unit Hierarchy Name</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[40%]">Details</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[15%]">Status</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[15%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => {
                let hierarchy = {};
                if (u.hierarchy) {
                  try {
                    hierarchy = typeof u.hierarchy === 'string' ? JSON.parse(u.hierarchy) : u.hierarchy;
                  } catch {
                    // ignore parse error
                  }
                }

                return (
                  <tr key={u.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-[13px] font-extrabold text-[#0d0d0d]">{hierarchy.unitHierarchyName || u.name}</p>
                    </td>
                    <td className="px-5 py-3">
                      {hierarchy.levels && hierarchy.levels.length > 0 ? (
                        <p className="text-[12px] font-medium text-slate-600">{hierarchy.levels.length} Levels</p>
                      ) : hierarchy.baseUnit ? (
                        <p className="text-[12px] font-medium text-slate-600">
                          1 {u.name} = <strong className="text-[var(--theme-primary)]">{hierarchy.conversionRate} {hierarchy.baseUnit}</strong>
                        </p>
                      ) : (
                        <p className="text-[12px] font-medium text-slate-400">—</p>
                      )}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <StatusToggle
                        on={(u.status || 'Active') === 'Active'}
                        label={u.name}
                        onChange={() => toggleStatus(u)}
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <RowActions>
                        <IconAction icon={Pen} label={`Edit ${u.name}`} title="Edit" onClick={() => openEdit(u)} />
                        <IconAction icon={Trash2} tone="danger" label={`Remove ${u.name}`} title="Remove" onClick={() => setConfirmDelete(u)} />
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-16 text-center">
                    <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-3">No Units match.</p>
                    <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                      <Plus size={12} /> Create the first one
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}

      <Modal
        open={modalOpen}
        draft={draft}
        setDraft={setDraft}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        mode={mode}
        accent={accent}
        showNotification={showNotification}
      />

      <BulkUploadModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        onSuccess={() => {
          setBulkModalOpen(false);
          fetchUnits();
        }}
        moduleName="Units"
        apiBasePath="/org/units"
        showNotification={showNotification}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">Are you sure you want to delete this unit?</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

UnitsTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
